// =====================================================
// MANAGER AUTHENTICATION API
// =====================================================

import type {
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { apiFetch, ApiClientError } from './api.core';
import type { Manager, ManagerSession, RegistrationOptions, LoginOptions } from './api.core';
import type { FeatureToggleFlagsResponse } from '@resida/api-types/feature-toggles';

export interface PolicyConsent {
  policyType: string;
  policyVersion: string;
}

/**
 * Start manager registration - get WebAuthn options
 */
export async function managerRegister(
  email: string
): Promise<RegistrationOptions> {
  return apiFetch<RegistrationOptions>('/landlord/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Complete manager registration - verify WebAuthn credential
 */
export async function managerRegisterVerify(
  managerId: string,
  response: RegistrationResponseJSON,
  policies: PolicyConsent[]
): Promise<{ manager: Manager }> {
  return apiFetch<{ manager: Manager }>('/landlord/api/auth/register/verify', {
    method: 'POST',
    body: JSON.stringify({ managerId, response, policies }),
  });
}

/**
 * Get login options for WebAuthn authentication
 * Email is optional - if not provided, uses discoverable credentials
 */
export async function managerLoginOptions(
  email?: string
): Promise<LoginOptions> {
  return apiFetch<LoginOptions>('/landlord/api/auth/login/options', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Complete login - verify WebAuthn authentication
 */
export async function managerLoginVerify(
  response: AuthenticationResponseJSON,
  sessionId: string
): Promise<{ manager: Manager }> {
  return apiFetch<{ manager: Manager }>('/landlord/api/auth/login/verify', {
    method: 'POST',
    body: JSON.stringify({ response, sessionId }),
  });
}

/**
 * Logout current manager session
 */
export async function managerLogout(): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/auth/logout', {
    method: 'POST',
  });
}

/**
 * Get current manager session
 * Returns null if not authenticated
 */
export async function managerSession(): Promise<ManagerSession | null> {
  try {
    return await apiFetch<ManagerSession>('/landlord/api/auth/session');
  } catch (error) {
    // Return null for auth errors (401, 403)
    if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
      return null;
    }
    throw error;
  }
}

// =====================================================
// FEATURE TOGGLES API
// =====================================================

/**
 * Get the caller's currently-resolved enabled feature toggle keys (BR-10/US-9).
 * Response type is derived from `@resida/api-types/feature-toggles`'s
 * FeatureToggleFlagsResponseSchema rather than hand-declared here.
 *
 * Unlike managerSession() above, errors are propagated normally (not swallowed to
 * null) — useAuth's checkSession() treats this call as best-effort and handles the
 * failure itself, so there is no reason to duplicate that swallowing here.
 */
export async function landlordFeatureToggles(): Promise<FeatureToggleFlagsResponse> {
  return apiFetch<FeatureToggleFlagsResponse>('/landlord/api/feature-toggles/me');
}

/**
 * Request a magic link to be sent to the email address
 * Platform defaults to 'web' for the landlord web app
 */
export async function managerRequestMagicLink(
  email: string,
  returnTo?: string
): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>(
    '/landlord/api/auth/magic-link/request',
    {
      method: 'POST',
      body: JSON.stringify({ email, platform: 'web', returnTo }),
    }
  );
}

/**
 * Verify a magic link token and establish a session
 */
export async function managerVerifyMagicLink(
  token: string
): Promise<{
  session: {
    identity: {
      id: string;
      email: string | null;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
      isActive: boolean;
      createdAt: string;
    };
    portfolios: Array<{
      portfolioId: string;
      permissions: string;
    }>;
    token: string;
    expiresAt: string;
    returnTo: string | null;
  };
}> {
  return apiFetch(
    '/landlord/api/auth/magic-link/verify',
    {
      method: 'POST',
      body: JSON.stringify({ token }),
    }
  );
}

/**
 * Request a magic link for manager registration
 * Platform defaults to 'web' for the landlord web app
 */
export async function managerRequestRegistrationMagicLink(
  email: string,
  policies: Array<{ policyType: string; policyVersion: string }>
): Promise<{ success: boolean; message: string }> {
  return apiFetch<{ success: boolean; message: string }>(
    '/landlord/api/auth/register/magic-link/request',
    {
      method: 'POST',
      body: JSON.stringify({ email, platform: 'web', policies }),
    }
  );
}

/**
 * Verify a magic link registration token and create account
 */
export async function managerVerifyRegistrationMagicLink(
  token: string
): Promise<{ manager: Manager; token: string; expiresAt: string }> {
  return apiFetch<{ manager: Manager; token: string; expiresAt: string }>(
    '/landlord/api/auth/register/magic-link/verify',
    {
      method: 'POST',
      body: JSON.stringify({ token }),
    }
  );
}

/**
 * Request an email OTP code for manager login
 */
export async function managerRequestEmailCode(
  email: string
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/auth/email-code/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Verify an email OTP code for manager login
 */
export async function managerVerifyEmailCode(
  email: string,
  code: string
): Promise<{
  session: {
    identity: { id: string; email: string | null; firstName: string | null; lastName: string | null; phone: string | null; isActive: boolean; createdAt: string };
    portfolios: Array<{ portfolioId: string; permissions: string }>;
    token: string;
    expiresAt: string;
  };
}> {
  return apiFetch('/landlord/api/auth/email-code/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

/**
 * Request an email OTP code for manager registration
 */
export async function managerRequestRegistrationEmailCode(
  email: string
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/auth/register/email-code/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Verify an email OTP code for manager registration
 */
export async function managerVerifyRegistrationEmailCode(
  email: string,
  code: string,
  policies?: Array<{ policyType: string; policyVersion: string }>
): Promise<{
  session: {
    identity: { id: string; email: string | null; firstName: string | null; lastName: string | null; phone: string | null; isActive: boolean; createdAt: string };
    portfolios: Array<{ portfolioId: string; permissions: string }>;
    token: string;
    expiresAt: string;
  };
}> {
  return apiFetch('/landlord/api/auth/register/email-code/verify', {
    method: 'POST',
    body: JSON.stringify({ email, code, policies }),
  });
}

/**
 * Freshness probe: resolves when the session was authenticated recently enough for money-moving routes.
 * A stale session triggers the shared re-authentication prompt (apiFetch's REAUTHENTICATION_REQUIRED
 * handling) and the returned promise never settles, so callers simply wait behind the prompt.
 */
export async function assertFreshSession(): Promise<void> {
  await apiFetch('/landlord/api/auth/session/fresh');
}
